import { Spacing, Text, Title } from '@vkontakte/vkui';
import classNames from 'clsx';
import img2x from 'img/tooltip/limit-downloads2x.png';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { fullTariff, limitForPublic, trialTariff } from 'reactApp/appHelpers/featuresHelpers/features/publicDownloadLimits';
import { loadPaidInfoRequest } from 'reactApp/modules/paidInfo/paidInfo.module';
import { PaidInfoSelectors } from 'reactApp/modules/paidInfo/paidInfo.selectors';
import { loadProductsAndOpenFastCheckout } from 'reactApp/modules/payment/payment.module';
import { UserStorageActions } from 'reactApp/modules/user/userStorage';
import { FloatingTooltip } from 'reactApp/ui//FloatingTooltip/FloatingTooltip';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { ETooltipPlacement } from 'reactApp/ui/FloatingTooltip/FloatingTooltip.types';
import { sendDwh } from 'reactApp/utils/ga';

import styles from './SharingTooltipPromo.css';

export const TOOLTIP_ID = 'tooltipLimitSharing';
export const MAX_SHOWN_COUNT = 3;

export interface SharingTooltipPromoProps {
    target: any;
    isMobile?: boolean;
    downloadLimits?: any;
}

const TRANSITION_TIMEOUT = 250;

const sendAnalytics = (action) => {
    sendDwh({ eventCategory: 'tooltip-limit-sharing', action, dwhData: { place: 'modal' } });
};

export const SharingTooltipPromo = ({ target, isMobile, downloadLimits }: SharingTooltipPromoProps): any => {
    const dispatch = useDispatch();
    const [showTooltip, setShowTooltip] = useState(false);
    const shownCount = Number(dispatch(UserStorageActions.get(TOOLTIP_ID)) || 0);
    const isUserWithoutPayment = useSelector(PaidInfoSelectors.isUserWithoutPayment);
    const productId = isUserWithoutPayment ? trialTariff : fullTariff;
    const total = downloadLimits?.total || limitForPublic;

    useEffect(() => {
        dispatch(loadPaidInfoRequest());
        setTimeout(() => {
            setShowTooltip(true);
        }, TRANSITION_TIMEOUT);
        if (shownCount <= MAX_SHOWN_COUNT) {
            dispatch(UserStorageActions.set(TOOLTIP_ID, shownCount + 1));
        }
    }, [dispatch]);

    const handleShow = useCallback(() => {
        emitAnalyticEvent(AnalyticEventNames.PUBLIC_LIMIT_PROMO_SHARING_SHOW);
        sendAnalytics('show');
        setShowTooltip(true);
    }, []);

    const handleClose = useCallback(() => {
        emitAnalyticEvent(AnalyticEventNames.PUBLIC_LIMIT_PROMO_SHARING_CLOSE);
        sendAnalytics('close');
        setShowTooltip(false);
    }, []);

    const handleClickBye = useCallback(() => {
        setShowTooltip(false);
        emitAnalyticEvent(AnalyticEventNames.PUBLIC_LIMIT_PROMO_SHARING_CLICK);
        sendAnalytics('click');
        dispatch(
            loadProductsAndOpenFastCheckout({
                productId,
                paySource: 'limit_sharing_modal',
                title: 'Подписка Mail Space: скачивания без лимита и другие опции',
                hideOtherTariffs: true,
            })
        );
    }, [productId]);

    if (!target?.current) {
        return null;
    }

    return (
        <>
            {showTooltip && shownCount <= MAX_SHOWN_COUNT && (
                <FloatingTooltip
                    target={target}
                    placement={isMobile ? ETooltipPlacement.bottom : ETooltipPlacement.right}
                    closable
                    closeOnOutsideClick
                    onClose={handleClose}
                    onShow={handleShow}
                    usePortal
                    withinModal
                >
                    <div className={classNames(styles.tooltip, { [styles.tooltipMobile]: isMobile })}>
                        <Title className={classNames(styles.tooltipTitle, { [styles.tooltipTitleMobile]: isMobile })}>
                            Скачивать смогут {total}&nbsp;раз&nbsp;за&nbsp;сутки
                        </Title>
                        <Spacing size={2} />
                        <Text className={classNames(styles.tooltipText, { [styles.tooltipTextMobile]: isMobile })}>
                            Оформите подписку Mail Space, если хотите снять лимит на&nbsp;скачивания
                        </Text>
                        <Spacing size={18} />
                        <Button onClick={handleClickBye} sizeMode={ButtonSizeMode.small} theme="vk" className={styles.button}>
                            {isUserWithoutPayment ? 'Снять лимит за 1 ₽' : 'Снять лимит'}
                        </Button>
                        <img
                            src={img2x}
                            alt="Снять лимит на скачивание с пабликов"
                            className={classNames(styles.image, { [styles.imageMobile]: isMobile })}
                        />
                    </div>
                </FloatingTooltip>
            )}
        </>
    );
};
